import React from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { takeWhile } from "ramda";
import { COLORS, BREAKPOINTS } from "@onelogin/react-components";

const TableCellInternal = styled.div`
  width: ${props => props.width};
`;

const TableTitleWrap = styled.div`
  position: relative;
`;

const TableTitle = styled.div`
  display: inline-block;
  background-color: white;
  color: ${props =>
    props.isHighlighted
      ? COLORS.STATUS_COLORS.SUCCESS
      : COLORS.GRAY_COLORS.GRAY_35};
  padding: 0px 10px;
  position: absolute;
  left: 10px;
  top: -10px;
`;

const TableRowInternal = styled.div`
  height: ${props => (props.isHeader ? "56px" : "50px")};
  background-color: ${props =>
    props.isSelected && !props.isHeader ? `#DDF0F6` : `${COLORS.WHITE}`};
  border-bottom: 1px solid ${COLORS.GRAY_COLORS.GRAY_94};
  margin-top: ${props => (props.isHighlighted ? "-1px" : "0px")};
  border-top-width: ${props =>
    props.isHeader || props.isHighlighted ? "1px" : "0px"};
  display: flex;
  flex: 0 0 ${props => (props.isHeader ? "56px" : "50px")};
  align-items: center;
  font-size: ${props => (props.isHeader ? 14 : 13)}px;
  font-weight: ${props => (props.isHeader ? 500 : 400)};
  padding-left: 24px;
  cursor: pointer;
  ${props => props.isHeader && "color: #002e3d"};
  ${props =>
    props.isHighlighted && `border: 1px solid ${COLORS.STATUS_COLORS.SUCCESS}`}

  &:hover {
    ${props =>
      !props.isHeader &&
      !props.isSelected &&
      `background-color: ${COLORS.ONELOGIN_GRAY_COLORS.ONELOGIN_GRAY_LIGHT}`}
  }

  @media screen and (max-width: ${BREAKPOINTS.PHONE_LARGE}) {
    padding-left: 20px;

    &:first-child {
      border-top-width: ${props => (props.hideTopBorderMobile ? 0 : "1px")};
    }
    &:hover {
      background-color: ${COLORS.WHITE};
    }
  }
`;

/* PORTAL-2630: The principle here is to allow cells to take up the space of all following
 * invisible cells, up until the next non-invisible cell. The resulting width would appear
 * as a css calc() line containing the current cell's width and the following invisible
 * cells widths (eg. "calc(33% + 17% + 17%)")
 */
const calculateCellWidth = (cells, index) => {
  const curCell = cells[index];
  const postCells = cells.slice(index + 1);
  const absorbedCells = takeWhile(cell => cell.hidden, postCells);
  if (absorbedCells.length === 0) {
    return cells[index].width;
  }
  return `calc(${[curCell, ...absorbedCells]
    .map(cell => cell.width)
    .join(" + ")})`;
};

export const TableRow = props => (
  <>
    {!props.isHeader && props.rowTitle && (
      <TableTitleWrap>
        <TableTitle isHighlighted={props.isHighlighted}>
          {props.rowTitle}
        </TableTitle>
      </TableTitleWrap>
    )}
    <TableRowInternal
      isHeader={props.isHeader}
      onClick={props.onClick}
      isHighlighted={props.isHighlighted}
      hideTopBorderMobile={props.hideTopBorderMobile}
      isSelected={props.noteId === props.currentNoteId}
    >
      {props.cells.map(
        (column, index) =>
          column.width !== undefined &&
          !column.hidden && (
            <TableCellInternal
              width={calculateCellWidth(props.cells, index)}
              key={index}
              onClick={() => {
                column.onClick && column.onClick(props.isClipped);
              }}
            >
              {column.content}
            </TableCellInternal>
          )
      )}
    </TableRowInternal>
  </>
);

TableRow.propTypes = {
  cells: PropTypes.arrayOf(
    PropTypes.shape({
      onClick: PropTypes.func,
      width: PropTypes.string, //width containing units
      content: PropTypes.oneOfType([PropTypes.node, PropTypes.string])
    })
  ),
  isHeader: PropTypes.bool,
  isHighlighted: PropTypes.bool,
  rowTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isClipped: PropTypes.bool.isRequired,
  onClick: PropTypes.func,
  hideTopBorderMobile: PropTypes.bool
};

export default TableRow;
